import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, XCircle, Info, Clock, Mail, User, Loader2, ExternalLink, Eye, Image } from 'lucide-react';
import englishService from '../services/englishService';

const Comments = () => {
  const [comments, setComments] = useState([]);
  const [filteredComments, setFilteredComments] = useState([]);
  const [contentCache, setContentCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const contentTypes = {
    blog: 'Blog',
    article: 'Article', 
    news: 'News',
    sccnMessage: 'SCCN Message',
    interview: 'Interview',
    speech: 'Speech',
    project: 'Project',
    event: 'Event',
    ovenfresh: 'OvenFresh'
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await englishService.comments.getAll();
      const commentsData = response.data || [];
      setComments(commentsData);
      setFilteredComments(commentsData);
      setError(null);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setError('Failed to load comments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchContentDetails = async (commentsData) => {
    try {
      setContentLoading(true);
      const cache = {};
      
      // Get all unique content items we need to fetch
      const uniqueContent = [];
      const seenContent = new Set();
      
      commentsData.forEach(comment => {
        const key = `${comment.contentType}-${comment.contentId}`;
        if (!seenContent.has(key)) {
          seenContent.add(key);
          uniqueContent.push({
            type: comment.contentType,
            id: comment.contentId,
            key: key
          });
        }
      });

      console.log('Fetching content for:', uniqueContent);

      // Fetch all content in parallel
      const contentPromises = uniqueContent.map(async (item) => {
        try {
          let allContent = [];
          
          // Fetch all content of this type
          if (item.type === 'blog') {
            const response = await englishService.blogs.getAll();
            allContent = response.data || [];
          } else if (item.type === 'article') {
            const response = await englishService.articles.getAll();
            allContent = response.data || [];
          } else if (item.type === 'news') {
            const response = await englishService.news.getAll();
            allContent = response.data || [];
          } else if (item.type === 'interview') {
            const response = await englishService.interviews.getAll();
            allContent = response.data || [];
          } else if (item.type === 'speech') {
            const response = await englishService.speeches.getAll();
            allContent = response.data || [];
          } else if (item.type === 'project') {
            const response = await englishService.projects.getAll();
            allContent = response.data || [];
          } else if (item.type === 'event') {
            const response = await englishService.events.getAll();
            allContent = response.data || [];
          } else if (item.type === 'ovenfresh') {
            const response = await englishService.ovenfresh.getAll();
            allContent = response.data || [];
          } else if (item.type === 'sccnMessage') {
            const response = await englishService.sccnMessages.getAll();
            allContent = response.data || [];
          }

          // Find the specific content item
          const foundContent = allContent.find(content => content._id === item.id);
          
          if (foundContent) {
            cache[item.key] = foundContent;
            console.log(`Found content for ${item.key}:`, foundContent.title || foundContent.name);
          } else {
            console.log(`Content not found for ${item.key}`);
            cache[item.key] = null;
          }
        } catch (error) {
          console.error(`Error fetching ${item.type}:`, error);
          cache[item.key] = null;
        }
      });

      await Promise.all(contentPromises);
      
      console.log('Content cache populated:', cache);
      setContentCache(cache);
    } catch (error) {
      console.error('Error fetching content details:', error);
    } finally {
      setContentLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  useEffect(() => {
    if (comments.length > 0) {
      fetchContentDetails(comments);
    }
  }, [comments]);

  const handleStatusChange = async (commentId, newStatus) => {
    try {
      setProcessingId(commentId);
      await englishService.comments.updateStatus(commentId, newStatus);
      
      setComments(comments.map(comment => 
        comment._id === commentId ? { ...comment, status: newStatus } : comment
      ));
    } catch (error) {
      console.error('Error updating comment status:', error);
      setError('Failed to update comment status');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        setProcessingId(commentId);
        await englishService.comments.delete(commentId);
        
        setComments(comments.filter(comment => comment._id !== commentId));
      } catch (error) {
        console.error('Error deleting comment:', error);
        setError('Failed to delete comment');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getContentUrl = (contentType, contentId) => {
    const baseUrl = 'https://rajendrashende.com';
    switch (contentType) {
      case 'blog':
        return `${baseUrl}/blogs/${contentId}`;
      case 'article':
        return `${baseUrl}/articles/${contentId}`;
      case 'news':
        return `${baseUrl}/news/${contentId}`;
      case 'interview':
        return `${baseUrl}/interviews/${contentId}`;
      case 'speech':
        return `${baseUrl}/speeches/${contentId}`;
      case 'project':
        return `${baseUrl}/projects/${contentId}`;
      case 'event':
        return `${baseUrl}/events/${contentId}`;
      case 'ovenfresh':
        return `${baseUrl}/ovenfresh/${contentId}`;
      case 'sccnMessage':
        return `${baseUrl}/sccn-messages/${contentId}`;
      default:
        return '#';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-red-500 flex items-center gap-2">
          <Info className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Comment Management</h1>
        <p className="text-gray-600">Manage and moderate user comments across all content</p>
        {contentLoading && (
          <div className="mt-2 text-sm text-blue-600 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading content details...
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Comments</p>
              <p className="text-2xl font-bold text-gray-800">{comments.length}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-sky-500" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {comments.filter(c => c.status === 'pending').length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Approved</p>
              <p className="text-2xl font-bold text-green-600">
                {comments.filter(c => c.status === 'approved').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Rejected</p>
              <p className="text-2xl font-bold text-red-600">
                {comments.filter(c => c.status === 'rejected').length}
              </p>
            </div>
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-6">
        {filteredComments.length > 0 ? (
          filteredComments.map((comment) => {
            const cacheKey = `${comment.contentType}-${comment.contentId}`;
            const content = contentCache[cacheKey];
            
            return (
              <div 
                key={comment._id} 
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
              >
                {/* Content Information Section - ALWAYS SHOW */}
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-4">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      {content && (content.thumbnail || content.image) ? (
                        <img
                          src={content.thumbnail || content.image}
                          alt="Content thumbnail"
                          className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-20 h-20 bg-gray-200 rounded-lg border border-gray-300 flex items-center justify-center ${content && (content.thumbnail || content.image) ? 'hidden' : 'flex'}`}
                      >
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                    </div>
                    
                    {/* Content Details */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {contentTypes[comment.contentType]}
                        </span>
                        <span className="text-xs text-gray-500">
                          ID: {comment.contentId}
                        </span>
                      </div>
                      
                      <h4 className="font-semibold text-gray-800 mb-2">
                        {content ? (content.title || content.name || 'Untitled Content') : 'Loading content...'}
                      </h4>
                      
                      {content && content.summary && (
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {content.summary}
                        </p>
                      )}
                      
                      {content && (content.date || content.createdAt) && (
                        <p className="text-xs text-gray-500 mb-3">
                          Published: {formatDate(content.date || content.createdAt)}
                        </p>
                      )}
                      
                      {/* View Button */}
                      
                       <a href={getContentUrl(comment.contentType, comment.contentId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View on Website
                        <ExternalLink className="w-3 h-3 ml-2" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Comment Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-gray-800 flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        {comment.name || 'Anonymous'}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(comment.status)}`}>
                        {comment.status || 'pending'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {comment.email || 'No email'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDate(comment.createdAt)}
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {comment.status !== 'approved' && (
                      <button
                        onClick={() => handleStatusChange(comment._id, 'approved')}
                        disabled={processingId === comment._id}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                        title="Approve"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    )}
                    
                    {comment.status !== 'rejected' && (
                      <button
                        onClick={() => handleStatusChange(comment._id, 'rejected')}
                        disabled={processingId === comment._id}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Reject"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleDelete(comment._id)}
                      disabled={processingId === comment._id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Delete"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {/* Comment Content */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{comment.comment || 'No comment text'}</p>
                </div>
                
                {/* Status indicator for approved comments */}
                {comment.status === 'approved' && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">This comment is live and visible to users</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="bg-white p-12 rounded-xl shadow-lg border border-gray-100 text-center">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No comments found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Comments;